import {
  isPricedValuation,
  priceUpdatedFor,
  valuationAmountText,
  valuationBadgeFor,
} from "../../../../../services/valuation";
import {
  byNumber,
  formatDate,
  getNumberFormat,
  sortRows,
  SortRule,
  toTone,
} from "../../../../../utils";
import {
  InvestmentHistorySeries,
  InvestmentHistorySeriesPoint,
} from "../../../../../services/investments";
import { GRAPH_COLORS } from "../../../../../utils/constants";
import { DateFormat } from "../../../../../utils/types";
import { Investments } from "../../../types";
import { isLedgerMode, presentedValueOf } from "../../../utils";
import { HoldingRow, SortKey, SortState } from "./types";

// This page's own money formatting, kept as the priced branch so adopting the
// shared state rule does not restyle every figure in the table.
const formatValue = (value: number): string => `${getNumberFormat(value)} €`;

// Ledger netGain closes on the whole broker value (securities + cash) and must never be
// re-derived here — the account page one click away reads the same field.
const gainOf = (investment: Investments): number | null => {
  if (isLedgerMode(investment.trackingMode)) {
    return investment.netGain;
  }
  if (investment.brokerValue === null) {
    return null;
  }
  return (
    investment.brokerValue +
    investment.withdrawnValue -
    investment.investedValue
  );
};

export const buildHoldings = (
  investments: Investments[],
  trendById: Map<number, number[]>,
): HoldingRow[] => {
  return investments.map((investment, index) => {
    const presentedValue = presentedValueOf(investment);
    const gain = gainOf(investment);
    const returnPercent =
      gain !== null && investment.investedValue > 0
        ? (gain / investment.investedValue) * 100
        : null;

    // Only a ledger investment has a provider price behind its value, so only a ledger row can
    // honestly say where that price came from; manual keeps its plain nullness rule.
    const valuation = investment.valuation ?? null;
    const hasLedgerState =
      isLedgerMode(investment.trackingMode) && valuation !== null;
    const badge = hasLedgerState ? valuationBadgeFor(valuation) : null;
    const isPriced = isPricedValuation({
      value: presentedValue,
      valuation,
    });
    const valueText = valuationAmountText({
      value: presentedValue,
      // A manual row has no state to route on, and passing one it does not own
      // would describe a typed-in figure as though a quote were behind it.
      valuation: hasLedgerState ? valuation : null,
      format: formatValue,
    });

    return {
      id: investment.id,
      name: investment.name,
      typeName: investment.investmentTypeName,
      typeColor: GRAPH_COLORS[index % GRAPH_COLORS.length],
      since: formatDate(investment.startDate, DateFormat.DMY_SPACE),
      sinceValue: new Date(investment.startDate).getTime(),
      value: presentedValue,
      invested: investment.investedValue,
      gain,
      gainTone: toTone(gain ?? 0),
      returnPercent,
      returnTone: toTone(returnPercent ?? 0),
      trend: trendById.get(investment.id) ?? [],
      badge,
      valueText,
      priceUpdated:
        hasLedgerState && isPriced ? priceUpdatedFor(investment) : null,
    };
  });
};

const SORT_RULES: Record<SortKey, SortRule<HoldingRow>> = {
  since: byNumber((row) => row.sinceValue),
  value: byNumber((row) => row.value),
  invested: byNumber((row) => row.invested),
  gain: byNumber((row) => row.gain),
  return: byNumber((row) => row.returnPercent),
};

export const sortHoldings = (
  rows: HoldingRow[],
  sort: SortState,
): HoldingRow[] => sortRows(rows, SORT_RULES, sort);

// The run of priced points at the end of the series: `Sparkline` takes plain numbers with no null to gap on, so a blocked month is cut rather than bridged into an invented segment.
// Cut, not dropped entirely — a position opened before the import horizon has blocked months at the *start* of nearly every window, and refusing the whole trend would leave a well-priced history with no shape.
const pricedTailOf = (points: InvestmentHistorySeriesPoint[]): number[] => {
  const tail: number[] = [];
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const value = points[index].brokerValue;
    if (value === null) {
      break;
    }
    tail.unshift(value);
  }
  return tail;
};

// Bounded by the series' own published range, so the trend never leans on lining up with the portfolio's dates.
export const sparklineOf = ({ points, range }: InvestmentHistorySeries) =>
  pricedTailOf(
    points.filter(
      (point) => point.date >= range.start && point.date <= range.end,
    ),
  );
